import { differenceInDays } from 'date-fns';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';

import { useSessionData } from '@confluence/session-data';
import { isMobileUserAgent, shouldShowMobileWeb } from '@confluence/mobile-detection';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useUserByLicensedProducts } from '@confluence/onboarding-helpers/entry-points/hooks/useUserByLicensedProducts';
import { useIsEstablishedUserTraitName } from '@confluence/onboarding-helpers/entry-points/hooks/useIsEstablishedUserTraitName';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';
import { fg } from '@confluence/feature-gating';
import { ENHANCE_ORIENTATION_TOUR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';

const GetPersonalizationQuery = gql`
	query useIsEligibleForEnhanceOrientationTourUserPersonalizationQuery($userId: String!)
	@experimental {
		experimentalUserPersonalization(userId: $userId) {
			attributes {
				name
				value
			}
		}
	}
`;

/**
 * Eligibility hook for enhance tour experiment with following requirements to be eligible
 * New users
 * Desktop only
 * First 7 days of onboarding
 * Enrollled in Statsig experiment
 * Return seperate values for land versus xflow, as well as onboarding state values for tourSeenAt and hasDismissedTour
 */
export const useIsEligibleForEnhanceOrientationTour = () => {
	const { userId } = useSessionData();
	const isEstablishedUserTraitName = useIsEstablishedUserTraitName();
	const isNav4Enabled = useIsNav4Enabled();

	// Onboarding state to check when they've seen the tour
	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([...Object.values(ENHANCE_ORIENTATION_TOUR_ONBOARDING_STATE_KEYS)]);

	const {
		data: userPersonalizationQueryData,
		loading: userPersonalizationQueryLoading,
		error: userPersonalizationQueryError,
	} = useQuery(GetPersonalizationQuery, {
		variables: {
			skip: !userId || !isNav4Enabled || !fg('cc-onboarding-enhance-orientation-tour-eligible'),
			userId,
		},
	});

	const isDesktopUser = !(isMobileUserAgent() || shouldShowMobileWeb());

	const { tourSeenAt, hasDismissedTour, hasSeenFinishNudge, hasCompletedTour } =
		deserializeState(onboardingStateData);

	const isInOnboardingPeriod =
		Boolean(tourSeenAt) && differenceInDays(new Date(), new Date(tourSeenAt)) <= 2;
	const tourExpired = Boolean(tourSeenAt) && differenceInDays(new Date(), new Date(tourSeenAt)) > 2;

	const ineligibleForTakeover = tourExpired;

	const attributes =
		userPersonalizationQueryData?.experimentalUserPersonalization?.attributes ?? [];

	const isEstablishedUser =
		ineligibleForTakeover ||
		attributes.find(
			(attr: { name: string; value: string }) => attr?.name === isEstablishedUserTraitName,
		)?.value === 'true';

	const isEstablishedUserReady = !userPersonalizationQueryLoading && !userPersonalizationQueryError;

	const isNotEstablishedUser = isEstablishedUserReady && !isEstablishedUser;

	const potentiallyEligibleUserForTour = !tourExpired && isDesktopUser && isNav4Enabled;

	const {
		isLandUser,
		isXflowUser,
		error: userTypeError,
		loading: userTypeLoading,
	} = useUserByLicensedProducts({
		skip: !potentiallyEligibleUserForTour || !fg('cc-onboarding-enhance-orientation-tour-eligible'),
	});

	const isGenerallyEligible = tourSeenAt !== undefined || Boolean(isNotEstablishedUser);

	if (userTypeLoading || onboardingStateLoading || userPersonalizationQueryLoading) {
		return {
			isEligibleForLand: false,
			isEligibleForXflow: false,
			loading: true,
			error: false,
			hasDismissedTour,
			hasSeenFinishNudge,
			hasCompletedTour,
			isXflowUser,
		};
	}

	if (userTypeError || userPersonalizationQueryError || onboardingStateError) {
		return {
			isEligibleForLand: false,
			isEligibleForXflow: false,
			loading: false,
			error: userTypeError || userPersonalizationQueryError || onboardingStateError,
			hasDismissedTour,
			hasSeenFinishNudge,
			hasCompletedTour,
			isXflowUser,
		};
	}

	return {
		isEligibleForLand:
			isLandUser && isGenerallyEligible && (tourSeenAt === undefined || isInOnboardingPeriod),
		isEligibleForXflow:
			isXflowUser && isGenerallyEligible && (tourSeenAt === undefined || isInOnboardingPeriod),
		loading: false,
		error: false,
		hasDismissedTour,
		hasCompletedTour,
		hasSeenFinishNudge,
		tourSeenAt,
		isXflowUser,
	};
};
